import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Pencil, Search, Upload, Loader2, MailCheck } from "lucide-react";

const SISTEMAS = ["MAIL", "FH", "PORTAL", "TELEFONO", "OTRO"];
const TIPOS = ["actividad", "hotel", "transfer", "otro"];

function emptyProvider() {
  return {
    nombre_sociedad: "",
    nombre_comercial: "",
    email: "",
    telefono: "",
    ciudad: "",
    pais: "",
    zona_opera: "",
    tipologia: "",
    tipo_servicio: "actividad",
    sistema_reserva: "MAIL",
    tipo_tour: "",
    cif: "",
    n_cuenta: "",
    codigo_swift: "",
    direccion: "",
    forma_pago: "",
    confirmacion: "",
    codigo_postal: "",
    factura_mensual: false,
    notas: "",
    como_solicitar_factura: "",
    persona_referencia: "",
    condiciones_cancelacion: "",
  };
}

export default function ProvidersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filterSistema, setFilterSistema] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyProvider());
  const [importing, setImporting] = useState(false);
  const [promoting, setPromoting] = useState(false);

  const fetchAll = async () => {
    const params = {};
    if (q) params.q = q;
    if (filterSistema) params.sistema_reserva = filterSistema;
    const { data } = await api.get("/providers", { params });
    setItems(data);
  };

  useEffect(() => { fetchAll(); }, [q, filterSistema]); // eslint-disable-line

  const openNew = () => { setEditing(null); setForm(emptyProvider()); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...emptyProvider(), ...p }); setOpen(true); };

  const onSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/providers/${editing.id}`, form);
        toast.success("Proveedor actualizado");
      } else {
        await api.post(`/providers`, form);
        toast.success("Proveedor creado");
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const onDelete = async (p) => {
    if (!confirm(`¿Borrar proveedor "${p.nombre_sociedad}"?`)) return;
    await api.delete(`/providers/${p.id}`);
    fetchAll();
  };

  const onPromoteOtroToMail = async () => {
    if (!window.confirm(
      "Esta migración revisa todos los proveedores marcados como OTRO que tengan email " +
      "y los pasa a MAIL para que puedas enviarles correos de reserva desde la app.\n\n" +
      "Es seguro ejecutarla varias veces (la 2ª vez no cambia nada). ¿Continuar?"
    )) return;
    setPromoting(true);
    try {
      const { data } = await api.post("/providers/promote-mail");
      if (data.promoted > 0) {
        toast.success(`Migración OK · ${data.promoted} proveedor${data.promoted > 1 ? "es" : ""} pasado${data.promoted > 1 ? "s" : ""} de OTRO a MAIL`);
      } else {
        toast.info("Nada que migrar — no quedan proveedores OTRO con email");
      }
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo ejecutar la migración");
    } finally {
      setPromoting(false);
    }
  };

  const onImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!confirm(`Importar proveedores desde "${file.name}"? Los proveedores existentes con el mismo nombre se actualizarán.`)) {
      e.target.value = "";
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/providers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importación OK · ${data.created} creados · ${data.updated} actualizados · ${data.skipped} vacíos`);
      if (data.errors?.length) {
        console.warn("Errores import:", data.errors);
        toast.warning(`${data.errors.length} errores (ver consola)`);
      }
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al importar");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="providers-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="vv-label">Catálogo</p>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Proveedores</h1>
          <p className="text-sm text-gray-500 mt-1">Gestiona la base de datos de hoteles, transferistas y actividades.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {user?.role === "admin" && (
            <Button
              variant="outline"
              onClick={onPromoteOtroToMail}
              disabled={promoting}
              className="border-violet-300 text-violet-700 hover:bg-violet-50"
              title="Pasa a sistema MAIL todos los proveedores que están como OTRO y tienen email"
              data-testid="promote-mail-button"
            >
              {promoting ? <Loader2 size={14} className="mr-1 animate-spin"/> : <MailCheck size={14} className="mr-1"/>}
              {promoting ? "Migrando…" : "Promover OTRO → MAIL"}
            </Button>
          )}
          <label className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 cursor-pointer" data-testid="import-providers-button">
            {importing ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
            {importing ? "Importando…" : "Importar CSV"}
            <input type="file" accept=".csv" className="hidden" onChange={onImportCSV} disabled={importing}/>
          </label>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={openNew} data-testid="create-provider-button">
            <Plus size={16} className="mr-1"/> Nuevo proveedor
          </Button>
        </div>
      </div>

      <div className="vv-card p-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, email, ciudad…" className="pl-9" data-testid="provider-search-input"/>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 uppercase tracking-wider">Sistema reserva</span>
          <Select value={filterSistema || "all"} onValueChange={(v) => setFilterSistema(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[140px] h-9" data-testid="provider-filter-sistema"><SelectValue placeholder="Todos"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {SISTEMAS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="vv-card overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-500">Sin proveedores. Crea el primero.</div>
        ) : (
          <table className="vv-table w-full">
            <thead>
              <tr>
                <th>Sociedad</th>
                <th>Comercial</th>
                <th>Email</th>
                <th>Ciudad</th>
                <th>Tipo</th>
                <th>Sistema</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id}>
                  <td className="font-medium">{p.nombre_sociedad}</td>
                  <td>{p.nombre_comercial || "—"}</td>
                  <td className="text-blue-700 mono text-xs">{p.email || "—"}</td>
                  <td>{p.ciudad || "—"}</td>
                  <td><span className="text-xs uppercase tracking-wider text-gray-500">{p.tipo_servicio}</span></td>
                  <td>
                    <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${p.sistema_reserva === "MAIL" ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                      {p.sistema_reserva}
                    </span>
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => openEdit(p)} className="text-gray-400 hover:text-blue-700 mr-3" title="Editar" data-testid={`edit-provider-${p.id}`}>
                      <Pencil size={14}/>
                    </button>
                    <button onClick={() => onDelete(p)} className="text-gray-400 hover:text-red-600" title="Borrar">
                      <Trash2 size={14}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar proveedor" : "Nuevo proveedor"}</DialogTitle></DialogHeader>
          <form onSubmit={onSave} className="grid grid-cols-2 gap-4">
            <div><Label className="vv-label">Nombre de la Sociedad *</Label><Input required value={form.nombre_sociedad} onChange={(e) => setForm({ ...form, nombre_sociedad: e.target.value })} data-testid="provider-nombre-input"/></div>
            <div><Label className="vv-label">Nombre Comercial</Label><Input value={form.nombre_comercial || ""} onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })}/></div>
            <div><Label className="vv-label">Email</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="provider-email-input"/></div>
            <div><Label className="vv-label">Teléfono</Label><Input value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })}/></div>
            <div><Label className="vv-label">Ciudad</Label><Input value={form.ciudad || ""} onChange={(e) => setForm({ ...form, ciudad: e.target.value })}/></div>
            <div><Label className="vv-label">País</Label><Input value={form.pais || ""} onChange={(e) => setForm({ ...form, pais: e.target.value })}/></div>
            <div><Label className="vv-label">Zona donde opera</Label><Input value={form.zona_opera || ""} onChange={(e) => setForm({ ...form, zona_opera: e.target.value })}/></div>
            <div><Label className="vv-label">Tipología actividad</Label><Input value={form.tipologia || ""} onChange={(e) => setForm({ ...form, tipologia: e.target.value })} placeholder="Gastronomía, Cultural…"/></div>
            <div>
              <Label className="vv-label">Tipo servicio</Label>
              <Select value={form.tipo_servicio} onValueChange={(v) => setForm({ ...form, tipo_servicio: v })}>
                <SelectTrigger data-testid="provider-tipo-select"><SelectValue/></SelectTrigger>
                <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="vv-label">Sistema de Reserva</Label>
              <Select value={form.sistema_reserva} onValueChange={(v) => setForm({ ...form, sistema_reserva: v })}>
                <SelectTrigger data-testid="provider-sistema-select"><SelectValue/></SelectTrigger>
                <SelectContent>{SISTEMAS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="vv-label">Tipo de Tour</Label><Input value={form.tipo_tour || ""} onChange={(e) => setForm({ ...form, tipo_tour: e.target.value })}/></div>
            <div><Label className="vv-label">CIF</Label><Input value={form.cif || ""} onChange={(e) => setForm({ ...form, cif: e.target.value })}/></div>
            <div><Label className="vv-label">Nº Cuenta</Label><Input value={form.n_cuenta || ""} onChange={(e) => setForm({ ...form, n_cuenta: e.target.value })}/></div>
            <div><Label className="vv-label">Código Swift</Label><Input value={form.codigo_swift || ""} onChange={(e) => setForm({ ...form, codigo_swift: e.target.value })}/></div>
            <div className="col-span-2"><Label className="vv-label">Dirección</Label><Input value={form.direccion || ""} onChange={(e) => setForm({ ...form, direccion: e.target.value })}/></div>
            <div><Label className="vv-label">Forma de pago</Label><Input value={form.forma_pago || ""} onChange={(e) => setForm({ ...form, forma_pago: e.target.value })}/></div>
            <div><Label className="vv-label">Confirmación</Label><Input value={form.confirmacion || ""} onChange={(e) => setForm({ ...form, confirmacion: e.target.value })} placeholder="VOUCHER…"/></div>
            <div><Label className="vv-label">Código Postal</Label><Input value={form.codigo_postal || ""} onChange={(e) => setForm({ ...form, codigo_postal: e.target.value })}/></div>
            <div className="flex items-center gap-2 mt-6">
              <Switch checked={!!form.factura_mensual} onCheckedChange={(v) => setForm({ ...form, factura_mensual: v })} id="factura"/>
              <Label htmlFor="factura" className="text-sm">Factura mensual</Label>
            </div>
            <div><Label className="vv-label">Persona de Referencia</Label><Input value={form.persona_referencia || ""} onChange={(e) => setForm({ ...form, persona_referencia: e.target.value })}/></div>
            <div><Label className="vv-label">Cómo solicitar factura</Label><Input value={form.como_solicitar_factura || ""} onChange={(e) => setForm({ ...form, como_solicitar_factura: e.target.value })}/></div>
            <div className="col-span-2"><Label className="vv-label">Notas</Label><Textarea rows={2} value={form.notas || ""} onChange={(e) => setForm({ ...form, notas: e.target.value })}/></div>
            <div className="col-span-2"><Label className="vv-label">Condiciones de Cancelación</Label><Textarea rows={2} value={form.condiciones_cancelacion || ""} onChange={(e) => setForm({ ...form, condiciones_cancelacion: e.target.value })}/></div>

            <DialogFooter className="col-span-2 mt-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-provider-button">{editing ? "Guardar cambios" : "Crear"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
